import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { MmProjectService } from '../../../mm-project/mm-project.service';
import { first } from 'rxjs';
import {ProjectService} from "@mydocma/project";
import { PixFile as PixPixFile } from '@mydocma/pix-common/lib/pix-common/file/models/file.interface';
import { PixFile } from '../pix.service';
import {MediaQueryService} from "@mydocma/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import { TimeFrameEnum } from '@mydocma/pix-common';
import { Logger } from '@mydocma/common/src/lib/logger';
import { MmPixFileSelectionCacheService } from './pix-file-selection-cache.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
@UntilDestroy()
@Component({
    selector: 'mydocma-pix-dialog',
    styleUrls: ['./pix-dialog.component.scss'],
    templateUrl: './pix-dialog.component.html',
  }
)
export class PixDialogComponent implements OnInit {
private fileApiService: any;
private pixTagService: any;
  project: any;
  selectedFiles: PixPixFile[] = [];
  showFooter = true;
  importing = false;
  public buttonText: string = '';
    public TimeFrameEnum = TimeFrameEnum;
  public dialogData: any;
  public fileOrigin = { Pix: 'Pix', Report: 'Report' };
  public isMobile = this.deviceService.isMobil();
  constructor(
    public dialogRef: MatLegacyDialogRef<PixDialogComponent>,
    protected mediaQueryService: MediaQueryService,
    @Inject(MAT_LEGACY_DIALOG_DATA) public data: any,
    private projectService: ProjectService,
    private translate: TranslateService,
    private readonly logger: Logger,
    public deviceService: DeviceService,
    public readonly fileSelectionCacheService: MmPixFileSelectionCacheService
  ) {
    this.dialogData = data;
  }

    // Add button handlers
  onCancel() {
    this.dialogRef.close();
  }


 onReady(event) {

  if (event && event.services && event.services.fileApiService) {
    this.fileApiService = event.services.fileApiService;
    this.pixTagService = event.services.pixTagService;
  }
}

  async onDone() {
    if (this.importing) {
      return;
    }
    if (!this.fileApiService || !this.selectedFiles || this.selectedFiles.length === 0) {
      this.dialogRef.close({ selectedFiles: [] });
      return;
    }
    this.importing = true;
    try {
      await this.importSelectedFiles();
    } finally {
      this.importing = false;
    }
  }

  private async importSelectedFiles() {
    const pixFileArray: any[] = [];
    const isSingleFile = this.selectedFiles.length === 1;
    for (const pixMeta of this.selectedFiles) {
      try {
        // Await blob from API
        const blob = await this.fileApiService.getFileAsyncAsBlob$(pixMeta.id).toPromise();
        // Compose previewable PixFile object
        const fileName = pixMeta.originalFileName;
        // Set lastModified from timestamp for correct recording date
        const lastModified = pixMeta.timestamp ? new Date(pixMeta.timestamp).getTime() : Date.now();
        // Prepare tag names to append to description
        let tagNamesConcat = '';
        let tagNamesArr: string[] = [];
        if (pixMeta.tags && Array.isArray(pixMeta.tags)) {
          if (this.pixTagService && typeof this.pixTagService.getTagById === 'function') {
            for (const tagId of pixMeta.tags) {
              const tag = await this.pixTagService.getTagById(tagId);
              if (tag && tag.name) {
                tagNamesArr.push(tag.name);
              }
            }
          }
        }
        if (tagNamesArr.length > 0) {
          tagNamesConcat =  tagNamesArr.join(', ');
        }
        // Create PixFile object with required id property
        const pixFile: PixFile = Object.assign(
          new File([blob], fileName, {
            type: blob.type || 'image/jpeg',
            lastModified
          }),
          {
            id: pixMeta.id,
            title: pixMeta.title,
            desc: (pixMeta.description ? pixMeta.description + '\n ' : '') + tagNamesConcat,
            date: pixMeta.timestamp,
            tags: pixMeta.tags
          }
        );
        pixFileArray.push(pixFile);
      } catch (err) {
        this.logger.error(err, '', 'PixDialogComponent');
      }
    }
    this.dialogRef.close({ selectedFiles: pixFileArray });
  }


  async ngOnInit() {
    if (this.mediaQueryService.isXs) {
      this.dialogRef.addPanelClass('mobile');
    } else {
      this.dialogRef.removePanelClass('mobile');
    }

    this.mediaQueryService.onMobileChanged
      .pipe(untilDestroyed(this))
      .subscribe(isXs => {
        if (isXs) {
          this.dialogRef.addPanelClass('mobile');
        } else {
          this.dialogRef.removePanelClass('mobile');
        }
      });
    // Set project from project service if not already passed in data
    if (!this.dialogData.project) {
      this.project = await this.projectService.currentProject.pipe(first()).toPromise();
    } else {
      this.project = this.dialogData.project;
    }
    // Ensure project.id is always set (map Id to id if needed)
    if (this.project && !this.project.id && this.project.Id) {
      this.project.id = this.project.Id;
    }
    // Defensive logging and error handling
    if (!this.project) {
      this.logger.error('PixDialogComponent: project is undefined or null!', '', 'PixDialogComponent');
    } else if (!this.project.id) {
      this.logger.error('PixDialogComponent: project.id is undefined or null!', '', 'PixDialogComponent');
    }

    // Set default button text
    this.buttonText = this.dialogData.submitDefaultText ? this.translate.instant(this.dialogData.submitDefaultText) : this.translate.instant('pix.importSelection');
  }

  changeSelection(event: any) {
    this.selectedFiles = event;
    // Update buttonText based on selection
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      const count = this.selectedFiles.length;
      // Use submitActionText with {0} replaced by count and correct pluralization
      let picsText = count === 1 ? this.translate.instant('pix.pic') : this.translate.instant('pix.pics');
      let actionText = this.dialogData.submitActionText ? this.translate.instant(this.dialogData.submitActionText) : this.translate.instant('pix.importSelection');
      actionText = actionText.replace('{0}', count).replace('{pics}', picsText);
      this.buttonText = actionText;
    } else {
      this.buttonText = this.dialogData.submitDefaultText ? this.translate.instant(this.dialogData.submitDefaultText) : this.translate.instant('pix.importSelection');
    }
      // Do not close dialog on selection change
  }

  onTagTreeVisibilityChanged(event: boolean) {
    this.showFooter = !event;
  }
}
